import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/RichTextEditor";
import ImageCropModal from "@/components/ImageCropModal";
import { LogOut, Plus, Edit2, Trash2, Upload, X, Compass, Star, Image as ImageIcon, GripVertical, Eye, EyeOff, FileText } from "lucide-react";

const CATEGORIES = ["Group Tours", "Customized Tours"];
const REGIONS = ["Europe", "Asia", "North America", "South America", "Antarctica", "Africa", "Oceania", "India"];
const TYPE_OPTIONS = ["Beach", "Mountain", "Desert", "Jungle", "Island", "Heritage"];
const STATUSES = ["Pending", "Contacted", "Confirmed"];

const emptyTour = () => ({
  title:"", slug:"", metaTitle:"", metaDescription:"", ogImage:"", category:"Group Tours", region:"Europe", durationDays:7, startingPrice:50000,
  overview:"", highlights:[], inclusions:[], exclusions:[], itinerary:[],
  departureDates:[], coverImage:"", gallery:[], featured:false, active:true,
  types:[], termsConditions:"", paymentSchedule:"", cancellationPolicy:"", mapEmbed:""
});

const emptyPost = () => ({
  title:"", slug:"", summary:"", coverImage:"", body:"", author:"Global Compass",
  keywords:"", metaTitle:"", metaDescription:"", ogImage:"", published:false
});

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("tours");
  const [tours, setTours] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState({clientName:"", destinationVisited:"", reviewText:"", rating:5, photoUrl:""});
  const [testUploading, setTestUploading] = useState(false);
  const testFileRef = useRef();
  const [dragIndex, setDragIndex] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [newG, setNewG] = useState({category:"", imageUrl:"", caption:""});
  const [editingGallery, setEditingGallery] = useState(null);
  const [gUploading, setGUploading] = useState(false);
  const gFileRef = useRef();
  const [posters, setPosters] = useState([]);
  const [newPoster, setNewPoster] = useState({imageUrl:"", caption:"", tourId:""});
  const [posterUploading, setPosterUploading] = useState(false);
  const posterFileRef = useRef();
  const [editingPoster, setEditingPoster] = useState(null);
  const [posterEditUploading, setPosterEditUploading] = useState(false);
  const posterEditFileRef = useRef();
  const [aboutForm, setAboutForm] = useState(null);
  const [homeStats, setHomeStats] = useState([]);
  const [homeIntro, setHomeIntro] = useState("");
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);
  const [editingSlide, setEditingSlide] = useState(null);
  const [homeBandImage, setHomeBandImage] = useState("");

  const load = async () => {
    const [a,b,c,g,p,ab,hc,bl,hs] = await Promise.all([
      api.get("/admin/tours"), api.get("/admin/inquiries"), api.get("/testimonials"), api.get("/gallery"),
      api.get("/featured-posters"), api.get("/about"), api.get("/home-content"), api.get("/admin/blog"), api.get("/admin/hero-slides")
    ]);
    setTours(a.data); setInquiries(b.data); setTestimonials(c.data); setGallery(g.data);
    setPosters(p.data);
    setHomeStats(hc.data.stats || []);
    setHomeIntro(hc.data.intro || "");
    setHomeBandImage(hc.data.bandImage || "");
    setBlogPosts(bl.data);
    setHeroSlides(hs.data);
    setAboutForm({
      eyebrow: ab.data.eyebrow || "",
      heading: ab.data.heading || "",
      sections: ab.data.sections && ab.data.sections.length
        ? ab.data.sections
        : (ab.data.paragraphs || []).map((p, i) => ({ title: ["Company Background","Our Achievements","Our Vision"][i] || "Section", body: p })),
      highlights: ab.data.highlights || ["", "", "", ""],
      affiliations: ab.data.affiliations || [],
    });
  };
  useEffect(() => { load(); }, []);

  const saveTour = async (t) => {
    try {
      const payload = { ...t,
        durationDays: Number(t.durationDays), startingPrice: Number(t.startingPrice),
        highlights: Array.isArray(t.highlights) ? t.highlights : String(t.highlights).split("\n").filter(Boolean),
        inclusions: Array.isArray(t.inclusions) ? t.inclusions : String(t.inclusions).split("\n").filter(Boolean),
        exclusions: Array.isArray(t.exclusions) ? t.exclusions : String(t.exclusions).split("\n").filter(Boolean),
        departureDates: Array.isArray(t.departureDates) ? t.departureDates : String(t.departureDates).split(",").map(s=>s.trim()).filter(Boolean),
        gallery: Array.isArray(t.gallery) ? t.gallery : String(t.gallery).split("\n").filter(Boolean),
        itinerary: t.itinerary || [],
      };
      if (t.id) await api.put(`/admin/tours/${t.id}`, payload);
      else await api.post("/admin/tours", payload);
      toast.success("Tour saved");
      setEditing(null); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };
  const deleteTour = async (id) => {
    if (!window.confirm("Delete this tour?")) return;
    await api.delete(`/admin/tours/${id}`); toast.success("Tour deleted"); await load();
  };
  const toggleTourActive = async (t) => {
    try {
      await api.put(`/admin/tours/${t.id}`, { ...t, active: t.active === false });
      toast.success(t.active === false ? "Tour is now Active" : "Tour set to Inactive");
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed to update tour"); }
  };
  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    await api.delete(`/admin/inquiries/${id}`); toast.success("Inquiry deleted"); await load();
  };
  const updateInquiry = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status }); toast.success("Status updated"); await load();
  };
  const addTestimonial = async () => {
    if (!newTestimonial.clientName || !newTestimonial.reviewText) return;
    await api.post("/admin/testimonials", { ...newTestimonial, rating: Number(newTestimonial.rating) });
    setNewTestimonial({clientName:"", destinationVisited:"", reviewText:"", rating:5, photoUrl:""});
    toast.success("Testimonial added"); await load();
  };
  const uploadTestimonialPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setTestUploading(true);
    try {
      const response = await api.post("/admin/upload", formData, { headers: {"Content-Type":"multipart/form-data"} });
      setNewTestimonial((t) => ({...t, photoUrl: response.data.url}));
      toast.success("Traveller photo uploaded");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Upload failed");
    } finally {
      setTestUploading(false);
      if (testFileRef.current) testFileRef.current.value = "";
    }
  };
  const reorderPosters = async (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...posters];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPosters(next);
    try {
      await api.post("/admin/featured-posters/reorder", { ids: next.map((p) => p.id) });
      toast.success("Poster order saved");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Failed to save order");
      await load();
    }
  };
  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); await load();
  };

  const uploadGallery = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setGUploading(true);
    try {
      const r = await api.post("/admin/upload", fd, { headers: {"Content-Type":"multipart/form-data"} });
      setNewG(x => ({...x, imageUrl: r.data.url}));
      toast.success("Image uploaded — now add a category and save");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setGUploading(false); if (gFileRef.current) gFileRef.current.value = ""; }
  };
  const addGallery = async () => {
    if (!newG.category.trim()) { toast.error("Please enter a category name"); return; }
    if (!newG.imageUrl) { toast.error("Please upload or paste an image first"); return; }
    try {
      await api.post("/admin/gallery", { ...newG, category: newG.category.trim() });
      setNewG({category:"", imageUrl:"", caption:""});
      toast.success("Image added to gallery"); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed to add"); }
  };
  const deleteGallery = async (id) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); await load();
  };
  const saveGalleryEdit = async () => {
    if (!editingGallery.category.trim()) { toast.error("Category is required"); return; }
    try {
      await api.put(`/admin/gallery/${editingGallery.id}`, { category: editingGallery.category.trim(), caption: editingGallery.caption });
      setEditingGallery(null); toast.success("Gallery image updated"); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed to update"); }
  };
  const uploadPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setPosterUploading(true);
    try {
      const response = await api.post("/admin/upload", formData, {
        headers: {"Content-Type":"multipart/form-data"},
      });
      setNewPoster((poster) => ({...poster, imageUrl: response.data.url}));
      toast.success("Poster uploaded — add an optional caption and save");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Upload failed");
    } finally {
      setPosterUploading(false);
      if (posterFileRef.current) posterFileRef.current.value = "";
    }
  };
  const addPoster = async () => {
    if (!newPoster.imageUrl) {
      toast.error("Please upload a poster first");
      return;
    }
    if (!newPoster.tourId) {
      toast.error("Please link this poster to a tour");
      return;
    }
    try {
      await api.post("/admin/featured-posters", newPoster);
      setNewPoster({imageUrl:"", caption:"", tourId:""});
      toast.success("Featured poster added");
      await load();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Failed to add poster");
    }
  };
  const deletePoster = async (id) => {
    if (!window.confirm("Remove this featured poster?")) return;
    try {
      await api.delete(`/admin/featured-posters/${id}`);
      toast.success("Featured poster removed");
      await load();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Failed to remove poster");
    }
  };
  const uploadPosterEdit = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setPosterEditUploading(true);
    try {
      const response = await api.post("/admin/upload", formData, {
        headers: {"Content-Type":"multipart/form-data"},
      });
      setEditingPoster((poster) => ({...poster, imageUrl: response.data.url}));
      toast.success("New poster image uploaded — save to apply");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Upload failed");
    } finally {
      setPosterEditUploading(false);
      if (posterEditFileRef.current) posterEditFileRef.current.value = "";
    }
  };
  const savePosterEdit = async () => {
    if (!editingPoster.imageUrl || !editingPoster.tourId) {
      toast.error("A poster image and linked tour are required");
      return;
    }
    try {
      await api.put(`/admin/featured-posters/${editingPoster.id}`, {
        imageUrl: editingPoster.imageUrl,
        caption: editingPoster.caption,
        tourId: editingPoster.tourId,
      });
      setEditingPoster(null);
      toast.success("Featured poster updated");
      await load();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Failed to update poster");
    }
  };
  const uploadAffiliationImage = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/admin/upload", formData, {
        headers: {"Content-Type":"multipart/form-data"},
      });
      setAboutForm((form) => ({
        ...form,
        affiliations: form.affiliations.map((item, itemIndex) => (
          itemIndex === index ? {...item, imageUrl: response.data.url} : item
        )),
      }));
      toast.success("Affiliation image uploaded");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };
  const updateAffiliation = (index, key, value) => {
    setAboutForm((form) => ({
      ...form,
      affiliations: form.affiliations.map((item, itemIndex) => (
        itemIndex === index ? {...item, [key]: value} : item
      )),
    }));
  };
  const addAffiliation = () => {
    setAboutForm((form) => ({
      ...form,
      affiliations: [...form.affiliations, {imageUrl:"", title:"", description:""}],
    }));
  };
  const deleteAffiliation = (index) => {
    setAboutForm((form) => ({
      ...form,
      affiliations: form.affiliations.filter((_, itemIndex) => itemIndex !== index),
    }));
  };
  const saveAbout = async () => {
    if (!aboutForm.heading.trim() || !aboutForm.sections.some((s) => s.body.trim())) {
      toast.error("Heading and at least one section cannot be empty");
      return;
    }
    const payload = {
      eyebrow: aboutForm.eyebrow,
      heading: aboutForm.heading,
      sections: aboutForm.sections.filter((s) => s.title.trim() && s.body.trim()),
      highlights: aboutForm.highlights.map((item) => item.trim()).filter(Boolean),
      affiliations: aboutForm.affiliations.filter((item) => (
        item.imageUrl && item.title.trim() && item.description.trim()
      )),
    };
    try { await api.put("/admin/about", payload); toast.success("About page updated"); await load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };

  const updateSection = (index, key, value) => {
    setAboutForm((form) => ({
      ...form,
      sections: form.sections.map((s, i) => (i === index ? { ...s, [key]: value } : s)),
    }));
  };
  const addSection = () => setAboutForm((form) => ({ ...form, sections: [...form.sections, { title: "", body: "" }] }));
  const deleteSection = (index) => setAboutForm((form) => ({ ...form, sections: form.sections.filter((_, i) => i !== index) }));

  const saveHome = async () => {
    const clean = homeStats.map((s) => ({ value: String(s.value).trim(), label: String(s.label).trim() })).filter((s) => s.value && s.label);
    if (clean.length === 0) { toast.error("Add at least one figure"); return; }
    try { await api.put("/admin/home-content", { stats: clean, intro: homeIntro, bandImage: homeBandImage }); toast.success("Home content updated"); await load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };

  const saveSlide = async (slide) => {
    try {
      if (slide.id) await api.put(`/admin/hero-slides/${slide.id}`, slide);
      else await api.post("/admin/hero-slides", slide);
      toast.success("Slide saved"); setEditingSlide(null); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };
  const deleteSlide = async (id) => {
    if (!window.confirm("Delete this hero slide?")) return;
    await api.delete(`/admin/hero-slides/${id}`); toast.success("Slide deleted"); await load();
  };

  const uploadHomeBand = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/admin/upload", fd, { headers: {"Content-Type":"multipart/form-data"} }); setHomeBandImage(r.data.url); toast.success("Image uploaded — click Save"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Upload failed"); }
  };

  const saveBlog = async (post) => {
    try {
      const payload = { ...post,
        keywords: Array.isArray(post.keywords) ? post.keywords : String(post.keywords||"").split(",").map(s=>s.trim()).filter(Boolean),
      };
      if (post.id) await api.put(`/admin/blog/${post.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Post saved"); setEditingPost(null); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };
  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await api.delete(`/admin/blog/${id}`); toast.success("Post deleted"); await load();
  };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="bg-maroon text-amber-50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icon-white.jpg" alt="Global Compass" className="w-10 h-10 rounded-full" />
            <div>
              <div className="font-display text-lg font-bold">Global Compass Admin Portal</div>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 px-4 py-2 rounded-full text-sm font-semibold" data-testid="admin-logout-btn">
            <LogOut size={16}/> Sign out
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-3 flex gap-2">
          {[["tours","Tours"],["inquiries",`Inquiries (${inquiries.filter(i=>i.status==="Pending").length})`],["testimonials","Testimonials"],["gallery",`Gallery (${gallery.length})`],["hero",`Hero Slides (${heroSlides.length})`],["blog",`Blog (${blogPosts.length})`],["home","Home Page"],["about","About Page"]].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab===k?"bg-amber-500 text-maroon":"bg-white/10 hover:bg-white/20"}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
          <button
            onClick={() => setTab("posters")}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${
              tab === "posters" ? "bg-amber-500 text-maroon" : "bg-white/10 hover:bg-white/20"
            }`}
            data-testid="tab-posters"
          >
            Featured ({posters.length})
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {tab === "tours" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-maroon">Manage Tours ({tours.length})</h2>
              <button onClick={()=>setEditing(emptyTour())} className="btn-gold px-5 h-11 rounded-full font-semibold inline-flex items-center gap-2" data-testid="new-tour-btn">
                <Plus size={18}/> New Tour
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {tours.map(t => (
                <div key={t.id} className="card-warm p-5 flex gap-4" data-testid={`admin-tour-${t.id}`}>
                  <img src={fileUrl(t.coverImage)} alt="" className={`w-24 h-24 rounded-lg object-cover bg-warm-surface ${t.active===false ? "grayscale opacity-60" : ""}`}/>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-amber-800 font-semibold uppercase tracking-widest">{t.category}</div>
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${t.active===false ? "bg-slate-200 text-slate-600" : "bg-green-100 text-green-800"}`} data-testid={`tour-status-${t.id}`}>
                        {t.active===false ? "Inactive" : "Active"}
                      </span>
                    </div>
                    <div className="font-display text-lg font-bold text-maroon leading-tight">{t.title}</div>
                    <div className="text-slate-600 text-sm">{t.durationDays}d · {t.region} · {inr(t.startingPrice)}</div>
                    <div className="mt-2 flex flex-wrap gap-3">
                      <button onClick={()=>setEditing(t)} className="text-amber-700 hover:text-amber-900 text-sm font-semibold inline-flex items-center gap-1"><Edit2 size={14}/> Edit</button>
                      <button onClick={()=>toggleTourActive(t)} className={`text-sm font-semibold inline-flex items-center gap-1 ${t.active===false ? "text-green-700 hover:text-green-900" : "text-slate-600 hover:text-slate-800"}`} data-testid={`tour-toggle-active-${t.id}`}>
                        {t.active===false ? <><Eye size={14}/> Set Active</> : <><EyeOff size={14}/> Set Inactive</>}
                      </button>
                      <button onClick={()=>deleteTour(t.id)} className="text-red-700 hover:text-red-900 text-sm font-semibold inline-flex items-center gap-1"><Trash2 size={14}/> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "inquiries" && (
          <>
            <h2 className="font-display text-2xl font-bold text-maroon mb-6">Customer Inquiries ({inquiries.length})</h2>
            <div className="bg-white rounded-2xl border border-amber-900/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-warm-surface text-maroon">
                  <tr>
                    {["Name","Email / Phone","Travelers","Preferred Date","Notes","Status","Actions"].map(h=>
                      <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map(i=>(
                    <tr key={i.id} className="border-t border-amber-900/10" data-testid={`inquiry-row-${i.id}`}>
                      <td className="px-4 py-3 font-semibold text-maroon">{i.customerName}</td>
                      <td className="px-4 py-3 text-slate-700">{i.email}<br/><span className="text-slate-500">{i.phone}</span></td>
                      <td className="px-4 py-3">{i.travelerCount}</td>
                      <td className="px-4 py-3">{i.preferredDepartureDate || "—"}</td>
                      <td className="px-4 py-3 max-w-xs text-slate-600 text-xs">{i.specialRequirements}</td>
                      <td className="px-4 py-3">
                        <select value={i.status} onChange={(e)=>updateInquiry(i.id, e.target.value)}
                          className={`px-3 py-2 rounded-full text-xs font-bold uppercase tracking-wider border-0 ${
                            i.status==="Pending"?"bg-amber-100 text-amber-800":
                            i.status==="Contacted"?"bg-blue-100 text-blue-800":"bg-green-100 text-green-800"}`}
                          data-testid={`inquiry-status-${i.id}`}>
                          {STATUSES.map(s=><option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={()=>deleteInquiry(i.id)} className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`inquiry-delete-${i.id}`}>
                          <Trash2 size={14}/> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {inquiries.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No inquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "testimonials" && (
          <>
            <h2 className="font-display text-2xl font-bold text-maroon mb-6">Testimonials</h2>
            <div className="card-warm p-6 mb-6 grid md:grid-cols-4 gap-3">
              <input placeholder="Client name" value={newTestimonial.clientName} onChange={(e)=>setNewTestimonial({...newTestimonial, clientName:e.target.value})}
                className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="new-testimonial-name"/>
              <input placeholder="Destination" value={newTestimonial.destinationVisited} onChange={(e)=>setNewTestimonial({...newTestimonial, destinationVisited:e.target.value})}
                className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="new-testimonial-dest"/>
              <input placeholder="Review text" value={newTestimonial.reviewText} onChange={(e)=>setNewTestimonial({...newTestimonial, reviewText:e.target.value})}
                className="md:col-span-2 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="new-testimonial-text"/>
              <select value={newTestimonial.rating} onChange={(e)=>setNewTestimonial({...newTestimonial, rating:e.target.value})}
                className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm">
                {[5,4,3,2,1].map(n=><option key={n}>{n}</option>)}
              </select>
              <button onClick={addTestimonial} className="btn-gold h-11 px-6 rounded-full font-semibold md:col-span-3" data-testid="new-testimonial-btn">Add Testimonial</button>
              <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center justify-center gap-2 text-sm" data-testid="testimonial-photo-upload-label">
                <Upload size={14}/>
                <input ref={testFileRef} type="file" accept="image/*" hidden onChange={uploadTestimonialPhoto} data-testid="testimonial-photo-upload-input"/>
                {testUploading ? "Uploading…" : "Upload Photo"}
              </label>
              {newTestimonial.photoUrl && (
                <img src={fileUrl(newTestimonial.photoUrl)} alt="Traveller preview" className="w-14 h-14 rounded-full object-cover ring-2 ring-amber-200" data-testid="testimonial-photo-preview"/>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {testimonials.map(t => (
                <div key={t.id} className="card-warm p-6" data-testid={`admin-testimonial-${t.id}`}>
                  <div className="flex text-amber-500 mb-2">
                    {Array.from({length:t.rating||5}).map((_,i)=><Star key={i} size={16} fill="currentColor"/>)}
                  </div>
                  <p className="italic text-slate-700">"{t.reviewText}"</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {t.photoUrl ? (
                        <img src={fileUrl(t.photoUrl)} alt={t.clientName} className="w-10 h-10 rounded-full object-cover ring-2 ring-amber-200"/>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-maroon text-amber-100 grid place-items-center font-display font-bold text-sm">
                          {(t.clientName||"?").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-maroon">{t.clientName}</div>
                        <div className="text-sm text-amber-800">{t.destinationVisited}</div>
                      </div>
                    </div>
                    <button onClick={()=>deleteTestimonial(t.id)} className="text-red-700 text-sm font-semibold inline-flex items-center gap-1"><Trash2 size={14}/></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "gallery" && (
          <>
            <h2 className="font-display text-2xl font-bold text-maroon mb-6">Gallery Images ({gallery.length})</h2>
            <div className="card-warm p-6 mb-8">
              <div className="grid md:grid-cols-4 gap-3 items-start">
                <input placeholder="Category name (e.g. Greenland 2025)" value={newG.category} onChange={(e)=>setNewG({...newG, category:e.target.value})}
                  className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="gallery-category-input"/>
                <input placeholder="Caption (optional)" value={newG.caption} onChange={(e)=>setNewG({...newG, caption:e.target.value})}
                  className="md:col-span-2 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="gallery-caption-input"/>
                <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center justify-center gap-2 text-sm">
                  <Upload size={14}/> <input ref={gFileRef} type="file" accept="image/*" hidden onChange={uploadGallery} data-testid="gallery-upload-input"/>
                  {gUploading ? "Uploading…" : "Upload Image"}
                </label>
              </div>
              {newG.imageUrl && (
                <div className="mt-4 flex items-end gap-4">
                  <img src={fileUrl(newG.imageUrl)} alt="preview" className="w-32 h-32 rounded-lg object-cover ring-1 ring-amber-900/10" data-testid="gallery-preview"/>
                  <button onClick={addGallery} className="btn-gold h-11 px-6 rounded-full font-semibold" data-testid="gallery-add-btn">Add to Gallery</button>
                </div>
              )}
            </div>
            {editingGallery && (
              <div className="card-warm p-6 mb-8" data-testid="gallery-editor">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-xl font-bold text-maroon">Edit Gallery Image</h3>
                  <button type="button" onClick={()=>setEditingGallery(null)} className="w-9 h-9 grid place-items-center text-slate-600 hover:bg-warm-surface rounded-lg" data-testid="gallery-editor-close"><X size={18}/></button>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <input value={editingGallery.category} onChange={(e)=>setEditingGallery({...editingGallery, category:e.target.value})} placeholder="Category name" className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="gallery-edit-category"/>
                  <input value={editingGallery.caption} onChange={(e)=>setEditingGallery({...editingGallery, caption:e.target.value})} placeholder="Caption (optional)" className="md:col-span-2 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="gallery-edit-caption"/>
                </div>
                <button type="button" onClick={saveGalleryEdit} className="btn-gold h-11 px-6 rounded-full font-semibold mt-4" data-testid="gallery-edit-save">Save Changes</button>
              </div>
            )}
            {gallery.length === 0 ? (
              <div className="text-center py-20 text-slate-500">
                <ImageIcon size={40} className="mx-auto mb-3 text-amber-700/40"/>
                <p>No gallery images yet. Upload an image and assign it a category above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {gallery.map(g => (
                  <div key={g.id} className="card-warm p-2 group relative" data-testid={`admin-gallery-${g.id}`}>
                    <img src={fileUrl(g.imageUrl)} alt={g.caption||g.category} className="w-full aspect-square object-cover rounded-lg"/>
                    <div className="px-1 pt-2 pb-1">
                      <div className="text-xs font-bold uppercase tracking-widest text-amber-800">{g.category}</div>
                      {g.caption && <div className="text-slate-600 text-xs line-clamp-1">{g.caption}</div>}
                    </div>
                    <button onClick={()=>setEditingGallery({id:g.id, category:g.category, caption:g.caption||""})} className="mb-1 ml-1 text-amber-800 hover:text-amber-950 text-xs font-semibold inline-flex items-center gap-1" data-testid={`gallery-edit-${g.id}`}><Edit2 size={13}/> Edit</button>
                    <button onClick={()=>deleteGallery(g.id)} className="absolute top-3 right-3 bg-white/90 text-red-700 hover:bg-white rounded-full p-1.5 shadow" data-testid={`gallery-delete-${g.id}`}>
                      <Trash2 size={15}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "posters" && (
          <>
            <h2 className="font-display text-2xl font-bold text-maroon mb-6">
              Featured Posters ({posters.length})
            </h2>
            <div className="card-warm p-6 mb-8">
              <div className="grid md:grid-cols-4 gap-3 items-start">
                <input
                  placeholder="Optional poster caption"
                  value={newPoster.caption}
                  onChange={(e) => setNewPoster({...newPoster, caption: e.target.value})}
                  className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"
                  data-testid="featured-poster-caption-input"
                />
                <select
                  value={newPoster.tourId}
                  onChange={(e) => setNewPoster({...newPoster, tourId: e.target.value})}
                  className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"
                  data-testid="featured-poster-tour-select"
                >
                  <option value="">Link to a tour</option>
                  {tours.map((tour) => (
                    <option key={tour.id} value={tour.id}>{tour.title}</option>
                  ))}
                </select>
                <label
                  className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center justify-center gap-2 text-sm"
                  data-testid="featured-poster-upload-label"
                >
                  <Upload size={14} />
                  <input
                    ref={posterFileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={uploadPoster}
                    data-testid="featured-poster-upload-input"
                  />
                  {posterUploading ? "Uploading…" : "Upload 4:5 Poster"}
                </label>
                <button
                  onClick={addPoster}
                  className="btn-gold h-11 px-6 rounded-full font-semibold"
                  data-testid="featured-poster-add-btn"
                >
                  Add Featured Poster
                </button>
              </div>
              {newPoster.imageUrl && (
                <img
                  src={fileUrl(newPoster.imageUrl)}
                  alt="Featured poster preview"
                  className="mt-5 w-40 aspect-[4/5] object-cover rounded-lg ring-1 ring-amber-900/10"
                  data-testid="featured-poster-preview"
                />
              )}
            </div>
            {editingPoster && (
              <div
                className="card-warm p-6 mb-8"
                data-testid="featured-poster-editor"
              >
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h3 className="font-display text-xl font-bold text-maroon">Edit Featured Poster</h3>
                  <button
                    type="button"
                    onClick={() => setEditingPoster(null)}
                    className="w-10 h-10 text-slate-600 hover:bg-warm-surface rounded-lg grid place-items-center"
                    aria-label="Close poster editor"
                    data-testid="featured-poster-editor-close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <input
                    value={editingPoster.imageUrl}
                    onChange={(e) => setEditingPoster({
                      ...editingPoster,
                      imageUrl: e.target.value,
                    })}
                    placeholder="Poster image URL"
                    className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"
                    data-testid="featured-poster-edit-image"
                  />
                  <input
                    value={editingPoster.caption || ""}
                    onChange={(e) => setEditingPoster({
                      ...editingPoster,
                      caption: e.target.value,
                    })}
                    placeholder="Poster caption"
                    className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"
                    data-testid="featured-poster-edit-caption"
                  />
                  <select
                    value={editingPoster.tourId || ""}
                    onChange={(e) => setEditingPoster({
                      ...editingPoster,
                      tourId: e.target.value,
                    })}
                    className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"
                    data-testid="featured-poster-edit-tour-select"
                  >
                    <option value="">Link to a tour</option>
                    {tours.map((tour) => (
                      <option key={tour.id} value={tour.id}>{tour.title}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <label
                    className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer inline-flex items-center gap-2 text-sm"
                    data-testid="featured-poster-edit-upload-label"
                  >
                    <Upload size={14} />
                    <input
                      ref={posterEditFileRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={uploadPosterEdit}
                      data-testid="featured-poster-edit-upload-input"
                    />
                    {posterEditUploading ? "Uploading…" : "Upload New Image"}
                  </label>
                  {editingPoster.imageUrl && (
                    <img
                      src={fileUrl(editingPoster.imageUrl)}
                      alt="Poster preview"
                      className="w-24 aspect-[4/5] object-cover rounded-lg ring-1 ring-amber-900/10"
                      data-testid="featured-poster-edit-preview"
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={savePosterEdit}
                  className="btn-gold h-11 px-6 rounded-full font-semibold mt-4"
                  data-testid="featured-poster-save-btn"
                >
                  Save Poster Changes
                </button>
              </div>
            )}
            {posters.length === 0 ? (
              <div className="text-center py-20 text-slate-500" data-testid="featured-posters-empty">
                <ImageIcon size={40} className="mx-auto mb-3 text-amber-700/40" />
                <p>No featured posters yet. Upload a 4:5 poster to show it on the home page.</p>
              </div>
            ) : (
              <>
                <p className="text-slate-500 text-sm mb-3 flex items-center gap-2" data-testid="poster-reorder-hint">
                  <GripVertical size={15} className="text-amber-700" /> Drag posters to set the order they appear in the home carousel.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {posters.map((poster, posterIndex) => (
                  <div
                    key={poster.id}
                    className={`card-warm p-2 relative cursor-move transition-opacity ${dragIndex === posterIndex ? "opacity-40" : ""}`}
                    draggable
                    onDragStart={() => setDragIndex(posterIndex)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { reorderPosters(dragIndex, posterIndex); setDragIndex(null); }}
                    onDragEnd={() => setDragIndex(null)}
                    data-testid={`admin-featured-poster-${poster.id}`}
                  >
                    <div className="absolute top-3 left-3 bg-white/90 text-maroon rounded-full p-1.5 shadow" title="Drag to reorder">
                      <GripVertical size={14} />
                    </div>
                    <img
                      src={fileUrl(poster.imageUrl)}
                      alt={poster.caption || "Featured poster"}
                      className="w-full aspect-[4/5] object-cover rounded-lg pointer-events-none"
                    />
                    {poster.caption && (
                      <div className="px-1 pt-2 text-slate-600 text-xs line-clamp-2">
                        {poster.caption}
                      </div>
                    )}
                    <div className="px-1 pt-1 text-amber-800 text-xs font-semibold line-clamp-1">
                      {tours.find((tour) => tour.id === poster.tourId)?.title || "No linked tour"}
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingPoster({
                        id: poster.id,
                        imageUrl: poster.imageUrl,
                        caption: poster.caption || "",
                        tourId: poster.tourId || "",
                      })}
                      className="mt-2 ml-1 text-amber-800 hover:text-amber-950 text-xs font-semibold inline-flex items-center gap-1"
                      data-testid={`featured-poster-edit-${poster.id}`}
                    >
                      <Edit2 size={13} /> Edit
                    </button>
                    <button
                      onClick={() => deletePoster(poster.id)}
                      className="absolute top-3 right-3 bg-white/90 text-red-700 hover:bg-white rounded-full p-1.5 shadow"
                      data-testid={`featured-poster-delete-${poster.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
                </div>
              </>
            )}
          </>
        )}

        {tab === "hero" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-maroon">Hero Slideshow ({heroSlides.length})</h2>
              <button onClick={()=>setEditingSlide({image:"",eyebrow:"",title:"",body:""})} className="btn-gold px-5 h-11 rounded-full font-semibold inline-flex items-center gap-2" data-testid="new-slide-btn">
                <Plus size={18}/> New Slide
              </button>
            </div>
            {heroSlides.length === 0 ? (
              <div className="text-center py-20 text-slate-500" data-testid="hero-admin-empty"><ImageIcon size={40} className="mx-auto mb-3 text-amber-700/40"/><p>No slides yet.</p></div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {heroSlides.map(slide => (
                  <div key={slide.id} className="card-warm p-4 flex gap-4" data-testid={`admin-slide-${slide.id}`}>
                    <img src={fileUrl(slide.image)} alt="" className="w-32 aspect-video rounded-lg object-cover bg-warm-surface shrink-0"/>
                    <div className="flex-1 min-w-0">
                      {slide.eyebrow && <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700">{slide.eyebrow}</div>}
                      <div className="font-display text-base font-bold text-maroon leading-tight mt-0.5 line-clamp-2">{slide.title}</div>
                      <div className="mt-2 flex flex-wrap gap-3">
                        <button onClick={()=>setEditingSlide(slide)} className="text-amber-700 hover:text-amber-900 text-sm font-semibold inline-flex items-center gap-1" data-testid={`slide-edit-${slide.id}`}><Edit2 size={14}/> Edit</button>
                        <button onClick={()=>deleteSlide(slide.id)} className="text-red-700 hover:text-red-900 text-sm font-semibold inline-flex items-center gap-1" data-testid={`slide-delete-${slide.id}`}><Trash2 size={14}/> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "blog" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-maroon">Blog Posts ({blogPosts.length})</h2>
              <button onClick={()=>setEditingPost(emptyPost())} className="btn-gold px-5 h-11 rounded-full font-semibold inline-flex items-center gap-2" data-testid="new-post-btn">
                <Plus size={18}/> New Post
              </button>
            </div>
            {blogPosts.length === 0 ? (
              <div className="text-center py-20 text-slate-500" data-testid="blog-admin-empty">
                <FileText size={40} className="mx-auto mb-3 text-amber-700/40"/>
                <p>No posts yet. Write your first travel story.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {blogPosts.map(post => (
                  <div key={post.id} className="card-warm p-5 flex gap-4" data-testid={`admin-post-${post.id}`}>
                    <img src={fileUrl(post.coverImage) || "/logo.jpg"} alt="" className="w-24 h-24 rounded-lg object-cover bg-warm-surface"/>
                    <div className="flex-1 min-w-0">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${post.published ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"}`} data-testid={`post-status-${post.id}`}>
                        {post.published ? "Published" : "Draft"}
                      </span>
                      <div className="font-display text-lg font-bold text-maroon leading-tight mt-1">{post.title}</div>
                      <div className="text-slate-600 text-sm line-clamp-1">{post.summary}</div>
                      <div className="mt-2 flex flex-wrap gap-3">
                        <button onClick={()=>setEditingPost(post)} className="text-amber-700 hover:text-amber-900 text-sm font-semibold inline-flex items-center gap-1" data-testid={`post-edit-${post.id}`}><Edit2 size={14}/> Edit</button>
                        <button onClick={()=>deleteBlog(post.id)} className="text-red-700 hover:text-red-900 text-sm font-semibold inline-flex items-center gap-1" data-testid={`post-delete-${post.id}`}><Trash2 size={14}/> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "home" && (
          <>
            <h2 className="font-display text-2xl font-bold text-maroon mb-6">Home Page — "Why our guests keep coming back" figures</h2>
            <div className="card-warm p-6 space-y-3 max-w-3xl mb-6">
              <label className="text-sm font-semibold text-maroon">Intro paragraph (maroon band, shown under the heading)</label>
              <textarea rows={4} value={homeIntro} onChange={(e)=>setHomeIntro(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="home-intro-input"/>
              <label className="text-sm font-semibold text-maroon block pt-2">Feature image (the tall photo beside the figures)</label>
              <div className="flex gap-2">
                <input value={homeBandImage} onChange={(e)=>setHomeBandImage(e.target.value)} placeholder="URL or upload →"
                  className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="home-band-image-input"/>
                <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center gap-1 text-sm">
                  <Upload size={14}/> <input type="file" accept="image/*" hidden onChange={uploadHomeBand} data-testid="home-band-image-upload"/> Upload
                </label>
              </div>
              {homeBandImage && <img src={fileUrl(homeBandImage)} alt="" className="w-40 aspect-[4/5] object-cover rounded-lg"/>}
              <button onClick={saveHome} className="btn-gold h-10 px-5 rounded-full font-semibold text-sm" data-testid="home-intro-save-btn">Save Intro &amp; Image</button>
            </div>
            <div className="card-warm p-6 space-y-4 max-w-3xl">
              <p className="text-slate-600 text-sm">Edit the four (or more) figures shown in the maroon band on the home page. "Value" is the big number, "Label" is the small caption beneath it.</p>
              <div className="space-y-3">
                {homeStats.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-center" data-testid={`home-stat-editor-${i}`}>
                    <input value={s.value} onChange={(e)=>setHomeStats(homeStats.map((x,ix)=>ix===i?{...x,value:e.target.value}:x))}
                      placeholder="1000+" className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid={`home-stat-value-${i}`}/>
                    <input value={s.label} onChange={(e)=>setHomeStats(homeStats.map((x,ix)=>ix===i?{...x,label:e.target.value}:x))}
                      placeholder="Happy Families" className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid={`home-stat-label-${i}`}/>
                    <button onClick={()=>setHomeStats(homeStats.filter((_,ix)=>ix!==i))} className="w-10 h-10 text-red-700 hover:bg-red-50 rounded-lg grid place-items-center" data-testid={`home-stat-delete-${i}`}><Trash2 size={16}/></button>
                  </div>
                ))}
              </div>
              <button onClick={()=>setHomeStats([...homeStats, {value:"", label:""}])} className="text-sm font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1" data-testid="home-stat-add-btn"><Plus size={14}/> Add Figure</button>
              <div><button onClick={saveHome} className="btn-gold h-11 px-6 rounded-full font-semibold" data-testid="home-save-btn">Save Home Figures</button></div>
            </div>
          </>
        )}

        {tab === "about" && aboutForm && (
          <>
            <h2 className="font-display text-2xl font-bold text-maroon mb-6">Edit About Us Page</h2>
            <div className="card-warm p-6 space-y-4 w-full">
              <div>
                <label className="text-sm font-semibold text-maroon">Eyebrow (small label above heading)</label>
                <input value={aboutForm.eyebrow} onChange={(e)=>setAboutForm({...aboutForm, eyebrow:e.target.value})}
                  className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="about-eyebrow-input"/>
              </div>
              <div>
                <label className="text-sm font-semibold text-maroon">Page Heading</label>
                <input value={aboutForm.heading} onChange={(e)=>setAboutForm({...aboutForm, heading:e.target.value})}
                  className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="about-heading-input"/>
              </div>
              <div className="pt-2 border-t border-amber-900/10">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <label className="text-sm font-semibold text-maroon">Content Sections (e.g. Company Background, Achievements, Vision)</label>
                  <button type="button" onClick={addSection} className="text-sm font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1" data-testid="about-section-add-btn"><Plus size={14}/> Add Section</button>
                </div>
                <div className="space-y-4">
                  {aboutForm.sections.map((section, index) => (
                    <div key={index} className="p-4 bg-ivory rounded-lg space-y-2" data-testid={`about-section-editor-${index}`}>
                      <div className="flex items-center gap-3">
                        <input value={section.title} onChange={(e)=>updateSection(index,"title",e.target.value)} placeholder="Section title"
                          className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-white text-sm font-semibold" data-testid={`about-section-title-${index}`}/>
                        <button type="button" onClick={()=>deleteSection(index)} className="w-10 h-10 text-red-700 hover:bg-red-50 rounded-lg grid place-items-center" data-testid={`about-section-delete-${index}`}><Trash2 size={16}/></button>
                      </div>
                      <textarea rows={4} value={section.body} onChange={(e)=>updateSection(index,"body",e.target.value)} placeholder="Section text (separate paragraphs with a blank line)"
                        className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-white text-sm" data-testid={`about-section-body-${index}`}/>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-maroon">
                  Highlight Card Text (icons stay fixed)
                </label>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  {aboutForm.highlights.map((highlight, index) => (
                    <input
                      key={index}
                      value={highlight}
                      onChange={(e) => setAboutForm({
                        ...aboutForm,
                        highlights: aboutForm.highlights.map((item, itemIndex) => (
                          itemIndex === index ? e.target.value : item
                        )),
                      })}
                      className="h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"
                      data-testid={`about-highlight-input-${index}`}
                    />
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t border-amber-900/10">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <label className="text-sm font-semibold text-maroon">Affiliations</label>
                  <button
                    type="button"
                    onClick={addAffiliation}
                    className="text-sm font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                    data-testid="about-affiliation-add-btn"
                  >
                    <Plus size={14} /> Add Affiliation
                  </button>
                </div>
                <div className="space-y-4">
                  {aboutForm.affiliations.map((affiliation, index) => (
                    <div
                      key={index}
                      className="grid sm:grid-cols-[96px_1fr_auto] gap-3 items-start p-3 bg-ivory rounded-lg"
                      data-testid={`about-affiliation-editor-${index}`}
                    >
                      <div>
                        {affiliation.imageUrl ? (
                          <img
                            src={fileUrl(affiliation.imageUrl)}
                            alt="Affiliation preview"
                            className="w-20 aspect-square object-contain rounded-lg bg-white"
                          />
                        ) : (
                          <div className="w-20 aspect-square bg-white rounded-lg grid place-items-center text-xs text-slate-500">
                            Logo
                          </div>
                        )}
                        <label
                          className="mt-2 text-xs text-amber-800 font-semibold cursor-pointer inline-flex items-center gap-1"
                          data-testid={`about-affiliation-upload-label-${index}`}
                        >
                          <Upload size={12} /> Upload
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => uploadAffiliationImage(e, index)}
                            data-testid={`about-affiliation-upload-input-${index}`}
                          />
                        </label>
                      </div>
                      <div className="space-y-2">
                        <input
                          placeholder="Square image URL or upload"
                          value={affiliation.imageUrl}
                          onChange={(e) => updateAffiliation(index, "imageUrl", e.target.value)}
                          className="w-full h-10 px-3 rounded-lg border-2 border-amber-900/15 bg-white text-sm"
                          data-testid={`about-affiliation-image-${index}`}
                        />
                        <input
                          placeholder="Affiliation title"
                          value={affiliation.title}
                          onChange={(e) => updateAffiliation(index, "title", e.target.value)}
                          className="w-full h-10 px-3 rounded-lg border-2 border-amber-900/15 bg-white text-sm"
                          data-testid={`about-affiliation-title-${index}`}
                        />
                        <textarea
                          rows={2}
                          placeholder="Affiliation description"
                          value={affiliation.description}
                          onChange={(e) => updateAffiliation(index, "description", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-white text-sm"
                          data-testid={`about-affiliation-description-${index}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteAffiliation(index)}
                        className="w-10 h-10 text-red-700 hover:bg-red-50 rounded-lg grid place-items-center"
                        aria-label="Remove affiliation"
                        data-testid={`about-affiliation-delete-${index}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={saveAbout} className="btn-gold h-11 px-6 rounded-full font-semibold" data-testid="about-save-btn">Save About Page</button>
            </div>
          </>
        )}
      </main>

      {editing && <TourEditor tour={editing} onClose={()=>setEditing(null)} onSave={saveTour}/>}
      {editingPost && <BlogEditor post={editingPost} onClose={()=>setEditingPost(null)} onSave={saveBlog}/>}
      {editingSlide && <HeroSlideEditor slide={editingSlide} onClose={()=>setEditingSlide(null)} onSave={saveSlide}/>}
    </div>
  );
}

function TourEditor({ tour, onClose, onSave }) {
  const [t, setT] = useState(() => ({
    ...tour,
    highlights: (tour.highlights||[]).join("\n"),
    inclusions: (tour.inclusions||[]).join("\n"),
    exclusions: (tour.exclusions||[]).join("\n"),
    gallery: (tour.gallery||[]).join("\n"),
    departureDates: (tour.departureDates||[]).join(", "),
  }));
  const [itinerary, setItinerary] = useState(tour.itinerary || []);
  const [uploading, setUploading] = useState(false);
  const [cropState, setCropState] = useState(null);
  const fileRef = useRef();
  const set = (k,v) => setT(x => ({...x, [k]:v}));

  const upload = async (e, target) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/admin/upload", fd, { headers: {"Content-Type":"multipart/form-data"} });
      set(target, r.data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const uploadToGallery = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/admin/upload", fd, { headers: {"Content-Type":"multipart/form-data"} });
      setT(x => ({...x, gallery: (x.gallery ? x.gallery + "\n" : "") + r.data.url}));
      toast.success("Image added to gallery");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const addDay = () => setItinerary([...itinerary, {dayNumber: itinerary.length+1, title:"", description:"", image:"", active:true}]);
  const updateDay = (i, k, v) => setItinerary(itinerary.map((d,idx)=>idx===i?{...d,[k]:v}:d));
  const removeDay = (i) => setItinerary(itinerary.filter((_,idx)=>idx!==i).map((d,idx)=>({...d, dayNumber: idx+1})));
  const onDaySelect = (e, i) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCropState({ src: reader.result, dayIndex: i });
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  const applyDayCrop = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", new File([blob], "day.jpg", { type: "image/jpeg" }));
      const r = await api.post("/admin/upload", fd, { headers: {"Content-Type":"multipart/form-data"} });
      updateDay(cropState.dayIndex, "image", r.data.url);
      toast.success("Day image cropped & uploaded");
      setCropState(null);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-amber-900/10 sticky top-0 bg-white z-10">
          <h3 className="font-display text-2xl font-bold text-maroon">{t.id ? "Edit Tour" : "New Tour"}</h3>
          <button onClick={onClose}><X/></button>
        </div>
        <div className="p-6 space-y-4">
          <input placeholder="Tour title" value={t.title} onChange={(e)=>set("title",e.target.value)}
            className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-base" data-testid="editor-title"/>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-maroon">SEO URL slug (leave blank to auto-generate)</label>
              <input placeholder="e.g. swiss-alps-europe-tour" value={t.slug||""} onChange={(e)=>set("slug",e.target.value)}
                className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="editor-slug"/>
              <div className="text-xs text-slate-500 mt-1">Page URL: /tours/{(t.slug||"").trim() || "auto-from-title"}</div>
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">SEO Page Title (browser tab & search results)</label>
              <input placeholder="Defaults to the tour title" value={t.metaTitle||""} onChange={(e)=>set("metaTitle",e.target.value)}
                className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="editor-meta-title"/>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-maroon">Meta Description (search & social preview, ~160 chars)</label>
              <textarea rows={3} maxLength={200} placeholder="Defaults to the overview" value={t.metaDescription||""} onChange={(e)=>set("metaDescription",e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="editor-meta-description"/>
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">Social Share Image (Open Graph — defaults to the site logo)</label>
              <div className="flex gap-2 mt-1">
                <input value={t.ogImage||""} onChange={(e)=>set("ogImage",e.target.value)} placeholder="URL or upload →"
                  className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="editor-og-image"/>
                <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center gap-1 text-sm">
                  <Upload size={14}/> <input type="file" accept="image/*" hidden onChange={(e)=>upload(e,"ogImage")} data-testid="editor-og-upload"/>
                  {uploading?"…":"Upload"}
                </label>
              </div>
              {t.ogImage && <img src={fileUrl(t.ogImage)} alt="" className="mt-2 w-40 aspect-[1.91/1] object-cover rounded-lg"/>}
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <select value={t.category} onChange={(e)=>set("category",e.target.value)} className="h-12 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-base">
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
            <select value={t.region} onChange={(e)=>set("region",e.target.value)} className="h-12 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-base">
              {REGIONS.map(c=><option key={c}>{c}</option>)}
            </select>
            <input type="number" placeholder="Days" value={t.durationDays} onChange={(e)=>set("durationDays",e.target.value)}
              className="h-12 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-base"/>
            <input type="number" placeholder="Starting Price (INR)" value={t.startingPrice} onChange={(e)=>set("startingPrice",e.target.value)}
              className="h-12 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-base"/>
          </div>
          <textarea placeholder="Overview" rows={3} value={t.overview} onChange={(e)=>set("overview",e.target.value)}
            className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-base"/>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-maroon">Cover Image</label>
              <div className="flex gap-2 mt-1">
                <input value={t.coverImage} onChange={(e)=>set("coverImage",e.target.value)} placeholder="URL or upload →"
                  className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"/>
                <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center gap-1 text-sm">
                  <Upload size={14}/> <input type="file" accept="image/*" hidden onChange={(e)=>upload(e,"coverImage")} data-testid="upload-cover"/>
                  {uploading?"…":"Upload"}
                </label>
              </div>
              {t.coverImage && <img src={fileUrl(t.coverImage)} alt="" className="mt-2 w-full aspect-video object-cover rounded-lg"/>}
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">Departure Dates (comma-separated YYYY-MM-DD)</label>
              <input value={t.departureDates} onChange={(e)=>set("departureDates",e.target.value)}
                placeholder="2026-04-12, 2026-05-15" className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1"/>
              <label className="flex items-center gap-2 mt-4">
                <input type="checkbox" checked={!!t.featured} onChange={(e)=>set("featured", e.target.checked)}/>
                <span className="text-sm font-semibold text-maroon">Show as Featured</span>
              </label>
              <label className="flex items-center gap-2 mt-3">
                <input type="checkbox" checked={t.active !== false} onChange={(e)=>set("active", e.target.checked)} data-testid="editor-active-toggle"/>
                <span className="text-sm font-semibold text-maroon">Active (visible on the website)</span>
              </label>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div><label className="text-sm font-semibold text-maroon">Highlights (one per line)</label>
              <textarea rows={4} value={t.highlights} onChange={(e)=>set("highlights",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1"/></div>
            <div><label className="text-sm font-semibold text-maroon">Inclusions (one per line)</label>
              <textarea rows={4} value={t.inclusions} onChange={(e)=>set("inclusions",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1"/></div>
            <div><label className="text-sm font-semibold text-maroon">Exclusions (one per line)</label>
              <textarea rows={4} value={t.exclusions} onChange={(e)=>set("exclusions",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1"/></div>
          </div>

          <div>
            <label className="text-sm font-semibold text-maroon">Trip Type (select all that apply)</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="editor-types">
              {TYPE_OPTIONS.map((label) => {
                const checked = (t.types || []).includes(label);
                return (
                  <button key={label} type="button"
                    onClick={()=>set("types", checked ? (t.types||[]).filter(x=>x!==label) : [...(t.types||[]), label])}
                    className={`px-4 h-10 rounded-full text-sm font-semibold border-2 transition-colors ${checked ? "bg-maroon text-amber-50 border-maroon" : "bg-ivory text-slate-700 border-amber-900/15 hover:border-amber-600"}`}
                    data-testid={`editor-type-${label.toLowerCase()}`}>{label}</button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-maroon">Itinerary</label>
              <button onClick={addDay} className="text-sm font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"><Plus size={14}/> Add Day</button>
            </div>
            <div className="space-y-2 mt-2">
              {itinerary.map((d,i)=>(
                <div key={i} className="rounded-lg border-2 border-amber-900/10 bg-warm-surface/40 p-2">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-1 h-11 px-3 rounded-lg border-2 border-amber-900/15 bg-warm-surface font-bold text-maroon grid place-items-center">D{d.dayNumber}</span>
                    <input value={d.title} onChange={(e)=>updateDay(i,"title",e.target.value)} placeholder="Day title" className="col-span-4 h-11 px-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"/>
                    <input value={d.description} onChange={(e)=>updateDay(i,"description",e.target.value)} placeholder="Description" className="col-span-6 h-11 px-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"/>
                    <button onClick={()=>removeDay(i)} className="col-span-1 h-11 text-red-700 hover:text-red-900"><Trash2 size={16}/></button>
                  </div>
                  <div className="flex items-center gap-3 mt-2 pl-1">
                    {d.image ? (
                      <img src={fileUrl(d.image)} alt="" className="w-20 aspect-square object-cover rounded-md border border-amber-900/10"/>
                    ) : (
                      <div className="w-20 aspect-square rounded-md border border-dashed border-amber-900/20 grid place-items-center text-[10px] text-slate-400 text-center">square photo</div>
                    )}
                    <label className="btn-maroon h-9 px-3 rounded-lg font-semibold cursor-pointer inline-flex items-center gap-1 text-xs">
                      <Upload size={12}/> <input type="file" accept="image/*" hidden onChange={(e)=>onDaySelect(e,i)} data-testid={`itinerary-image-upload-${i}`}/>
                      {d.image ? "Replace & crop" : "Add & crop photo"}
                    </label>
                    {d.image && <button type="button" onClick={()=>updateDay(i,"image","")} className="text-xs text-red-700 hover:text-red-900 font-semibold">Remove</button>}
                    <input value={d.image||""} onChange={(e)=>updateDay(i,"image",e.target.value)} placeholder="or paste image URL" className="flex-1 h-9 px-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-xs"/>
                  </div>
                  {d.image && (
                    <input value={d.imageCaption||""} onChange={(e)=>updateDay(i,"imageCaption",e.target.value)} placeholder="Optional photo caption" className="w-full h-9 px-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-xs mt-2" data-testid={`itinerary-caption-input-${i}`}/>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-maroon">Gallery Images (upload or paste URLs, one per line)</label>
              <label className="btn-maroon h-9 px-4 rounded-lg font-semibold cursor-pointer inline-flex items-center gap-1 text-xs">
                <Upload size={13}/> <input type="file" accept="image/*" hidden onChange={uploadToGallery} data-testid="upload-gallery"/>
                {uploading?"…":"Upload Image"}
              </label>
            </div>
            <textarea rows={3} value={t.gallery} onChange={(e)=>set("gallery",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1"/>
          </div>
          <div className="grid gap-4">
            <div><label className="text-sm font-semibold text-maroon">Terms &amp; Conditions (shown on this tour's page)</label>
              <textarea rows={3} value={t.termsConditions||""} onChange={(e)=>set("termsConditions",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="editor-terms"/></div>
            <div><label className="text-sm font-semibold text-maroon">Payment Schedule (shown on this tour's page)</label>
              <textarea rows={3} value={t.paymentSchedule||""} onChange={(e)=>set("paymentSchedule",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="editor-payment"/></div>
            <div><label className="text-sm font-semibold text-maroon">Cancellation Policy (shown on this tour's page)</label>
              <textarea rows={3} value={t.cancellationPolicy||""} onChange={(e)=>set("cancellationPolicy",e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="editor-cancellation"/></div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-amber-900/10 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-6 rounded-full font-semibold text-slate-700 hover:bg-warm-surface">Cancel</button>
          <button onClick={()=>onSave({...t, itinerary})} className="btn-gold h-11 px-6 rounded-full font-semibold min-w-[140px]" data-testid="editor-save-btn">Save Tour</button>
        </div>
      </div>
      {cropState && <ImageCropModal src={cropState.src} aspect={1} busy={uploading} onCancel={()=>setCropState(null)} onApply={applyDayCrop} />}
    </div>
  );
}


function BlogEditor({ post, onClose, onSave }) {
  const [p, setP] = useState(() => ({
    ...post,
    keywords: Array.isArray(post.keywords) ? post.keywords.join(", ") : (post.keywords || ""),
  }));
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const uploadField = async (e, target) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); setUploading(true);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(target, r.data.url); toast.success("Image uploaded");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  const uploadInline = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return fileUrl(r.data.url);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Upload failed"); throw err; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-amber-900/10 sticky top-0 bg-white z-20">
          <h3 className="font-display text-2xl font-bold text-maroon">{p.id ? "Edit Post" : "New Post"}</h3>
          <button onClick={onClose} data-testid="blog-editor-close"><X/></button>
        </div>
        <div className="p-6 space-y-4">
          <input placeholder="Post title" value={p.title} onChange={(e)=>set("title",e.target.value)}
            className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-base" data-testid="blog-title-input"/>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-maroon">URL slug (leave blank to auto-generate)</label>
              <input placeholder="e.g. best-time-to-visit-greenland" value={p.slug||""} onChange={(e)=>set("slug",e.target.value)}
                className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="blog-slug-input"/>
              <div className="text-xs text-slate-500 mt-1">Page URL: /blog/{(p.slug||"").trim() || "auto-from-title"}</div>
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">Author</label>
              <input value={p.author||""} onChange={(e)=>set("author",e.target.value)}
                className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="blog-author-input"/>
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Summary / excerpt (shown on cards)</label>
            <textarea rows={2} value={p.summary||""} onChange={(e)=>set("summary",e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="blog-summary-input"/>
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Cover Image</label>
            <div className="flex gap-2 mt-1">
              <input value={p.coverImage||""} onChange={(e)=>set("coverImage",e.target.value)} placeholder="URL or upload →"
                className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"/>
              <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center gap-1 text-sm">
                <Upload size={14}/> <input type="file" accept="image/*" hidden onChange={(e)=>uploadField(e,"coverImage")} data-testid="blog-cover-upload"/>
                {uploading?"…":"Upload"}
              </label>
            </div>
            {p.coverImage && <img src={fileUrl(p.coverImage)} alt="" className="mt-2 w-full max-w-md aspect-[16/9] object-cover rounded-lg"/>}
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Article Body</label>
            <div className="mt-1">
              <RichTextEditor value={p.body} onChange={(html)=>set("body",html)} onImageUpload={uploadInline}/>
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Keywords (comma-separated — power on-site search & SEO)</label>
            <input value={p.keywords} onChange={(e)=>set("keywords",e.target.value)} placeholder="greenland, arctic, kolkata, ভ্রমণ"
              className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="blog-keywords-input"/>
          </div>
          <div className="pt-4 border-t border-amber-900/10">
            <div className="text-sm font-bold text-maroon mb-3 uppercase tracking-widest">SEO</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-maroon">SEO Page Title (defaults to title)</label>
                <input value={p.metaTitle||""} onChange={(e)=>set("metaTitle",e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="blog-meta-title-input"/>
              </div>
              <div>
                <label className="text-sm font-semibold text-maroon">Social Share Image (defaults to cover, then logo)</label>
                <div className="flex gap-2 mt-1">
                  <input value={p.ogImage||""} onChange={(e)=>set("ogImage",e.target.value)} placeholder="URL or upload →"
                    className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm"/>
                  <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center gap-1 text-sm">
                    <Upload size={14}/> <input type="file" accept="image/*" hidden onChange={(e)=>uploadField(e,"ogImage")} data-testid="blog-og-upload"/>
                    {uploading?"…":"Upload"}
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-sm font-semibold text-maroon">Meta Description (defaults to summary, ~160 chars)</label>
              <textarea rows={2} maxLength={200} value={p.metaDescription||""} onChange={(e)=>set("metaDescription",e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="blog-meta-description-input"/>
            </div>
          </div>
          <label className="flex items-center gap-2 pt-2">
            <input type="checkbox" checked={!!p.published} onChange={(e)=>set("published",e.target.checked)} data-testid="blog-published-toggle"/>
            <span className="text-sm font-semibold text-maroon">Published (visible on the public blog)</span>
          </label>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-amber-900/10 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-6 rounded-full font-semibold text-slate-700 hover:bg-warm-surface">Cancel</button>
          <button onClick={()=>onSave(p)} disabled={!p.title.trim()} className="btn-gold h-11 px-6 rounded-full font-semibold min-w-[140px] disabled:opacity-60" data-testid="blog-save-btn">Save Post</button>
        </div>
      </div>
    </div>
  );
}

function HeroSlideEditor({ slide, onClose, onSave }) {
  const [s, setS] = useState(() => ({ ...slide }));
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); setUploading(true);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", r.data.url); toast.success("Image uploaded");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-amber-900/10 sticky top-0 bg-white z-10">
          <h3 className="font-display text-2xl font-bold text-maroon">{s.id ? "Edit Slide" : "New Slide"}</h3>
          <button onClick={onClose} data-testid="slide-editor-close"><X/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-maroon">Slide Image (wide / landscape works best)</label>
            <div className="flex gap-2 mt-1">
              <input value={s.image||""} onChange={(e)=>set("image",e.target.value)} placeholder="URL or upload →"
                className="flex-1 h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm" data-testid="slide-image-input"/>
              <label className="btn-maroon h-11 px-4 rounded-lg font-semibold cursor-pointer flex items-center gap-1 text-sm">
                <Upload size={14}/> <input type="file" accept="image/*" hidden onChange={uploadImage} data-testid="slide-image-upload"/>
                {uploading?"…":"Upload"}
              </label>
            </div>
            {s.image && <img src={fileUrl(s.image)} alt="" className="mt-2 w-full aspect-video object-cover rounded-lg"/>}
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Eyebrow badge (small label above the title)</label>
            <input value={s.eyebrow||""} onChange={(e)=>set("eyebrow",e.target.value)} placeholder="e.g. Group Tours from Kolkata"
              className="w-full h-11 px-4 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="slide-eyebrow-input"/>
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Headline (H1)</label>
            <textarea rows={2} value={s.title||""} onChange={(e)=>set("title",e.target.value)} placeholder="The big headline"
              className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="slide-title-input"/>
          </div>
          <div>
            <label className="text-sm font-semibold text-maroon">Paragraph</label>
            <textarea rows={3} value={s.body||""} onChange={(e)=>set("body",e.target.value)} placeholder="Supporting sentence"
              className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 bg-ivory text-sm mt-1" data-testid="slide-body-input"/>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-amber-900/10 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-6 rounded-full font-semibold text-slate-700 hover:bg-warm-surface">Cancel</button>
          <button onClick={()=>onSave(s)} disabled={!s.image || !s.title?.trim()} className="btn-gold h-11 px-6 rounded-full font-semibold min-w-[140px] disabled:opacity-60" data-testid="slide-save-btn">Save Slide</button>
        </div>
      </div>
    </div>
  );
}

